import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ShieldQuestion } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { LogoFull } from '@/components/layout/logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import {
  useCreateChallenge,
  useMfaStatus,
  useVerifyBackupCode,
  useVerifyCode,
} from '@/features/mfa/hooks/use-mfa';
import { parseMfaError } from '@/features/mfa/lib/parse-error';
import { supabase } from '@/lib/supabase';

import type { ChallengeResponse } from '@/features/mfa/types';

// /auth/mfa/challenge — pantalla post-login para promover AAL1 → AAL2.
// Aplica cuando el user ya está enrolado pero la sesión actual no tiene
// aal2 (login normal, sin trust-device cookie válida, o cookie expirada).
//
// Auth gate: requiere session. Si no hay session → /login.
// Nota: no chequeamos AAL aquí — el hook de enforcement decide cuándo
// llegamos. Si el user ya tiene AAL2 y navega manual a esta URL, la verify
// va a fallar de forma esperable.

export const Route = createFileRoute('/auth/mfa/challenge')({
  component: ChallengePage,
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({ to: '/login' });
    }
  },
});

type Mode = 'totp' | 'backup';

function ChallengePage() {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-4 py-12"
      style={{ background: 'var(--color-bg)' }}
    >
      <div className="w-full max-w-sm">
        <div className="mb-10 flex justify-center">
          <LogoFull />
        </div>
        <ChallengeCard />
        <p className="mt-8 text-center text-xs text-[color:var(--color-fg-subtle)]">
          Simplificalo · Verificación de seguridad
        </p>
      </div>
    </div>
  );
}

function ChallengeCard() {
  const [mode, setMode] = useState<Mode>('totp');
  const [challenge, setChallenge] = useState<ChallengeResponse | null>(null);
  const challengeMutation = useCreateChallenge();
  // Status devuelve factor_id del factor verificado — necesario en el body del
  // POST /v1/auth/mfa/challenge (zod-validado en el backend).
  const { data: status } = useMfaStatus();
  const factorId = status?.factor_id;
  const navigate = useNavigate();

  // Si el backend reporta trust_device_valid, el user no necesita pasar por
  // challenge — redirigir a /. Cubre navegación manual, bookmarks, y race
  // conditions entre login y status query.
  useEffect(() => {
    if (status?.trust_device_valid) {
      void navigate({ to: '/', replace: true });
    }
  }, [status, navigate]);

  // Trigger challenge on mount (TOTP mode only — backup code no necesita
  // challenge separado, el endpoint backend toma el plain_code directo).
  useEffect(() => {
    if (mode !== 'totp' || challenge) return;
    if (!factorId) return; // esperar a que status traiga el factor_id
    challengeMutation
      .mutateAsync({ factor_id: factorId })
      .then((res) => setChallenge(res))
      .catch(() => {
        toast.error('No pudimos iniciar la verificación. Refresca la página.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, factorId]);

  return (
    <div
      className="rounded-xl border p-6 shadow-2xl"
      style={{
        background: 'var(--color-bg-elevated)',
        borderColor: 'var(--color-border)',
      }}
    >
      <h1 className="mb-1 text-lg font-semibold tracking-tight">
        {mode === 'totp' ? 'Ingresá tu código MFA' : 'Código de respaldo'}
      </h1>
      <p className="mb-6 text-sm text-[color:var(--color-fg-muted)]">
        {mode === 'totp'
          ? 'Abre tu app authenticator e ingresa el código de 6 dígitos que muestre para tu cuenta de Simplificalo.'
          : 'Pega uno de tus 10 códigos de respaldo. Cada código sirve una sola vez.'}
      </p>

      {mode === 'totp' ? (
        challenge ? (
          <TotpForm challenge={challenge} />
        ) : (
          <FormSkeleton />
        )
      ) : (
        <BackupCodeForm />
      )}

      <div className="mt-6 space-y-2 border-t pt-4 text-sm" style={{ borderColor: 'var(--color-border)' }}>
        {mode === 'totp' ? (
          <button
            type="button"
            onClick={() => setMode('backup')}
            className="block text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
          >
            Usar código de respaldo →
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setMode('totp')}
            className="block text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
          >
            ← Volver al código de la app
          </button>
        )}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            toast.info(
              'Si perdiste el acceso, contacta a un owner para que te resetee MFA, o usa un código de respaldo si guardaste alguno.',
              { duration: 7000 },
            );
          }}
          className="flex items-center gap-1.5 text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
        >
          <ShieldQuestion className="h-3.5 w-3.5" aria-hidden />
          ¿Perdiste tu dispositivo?
        </a>
      </div>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function TotpForm({ challenge }: { readonly challenge: ChallengeResponse }) {
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [lockedUntilSec, setLockedUntilSec] = useState<number | null>(null);
  const navigate = useNavigate();
  const verify = useVerifyCode();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (code.length !== 6) return;
    try {
      const res = await verify.mutateAsync({
        factor_id: challenge.factor_id,
        challenge_id: challenge.challenge_id,
        code,
        trust_device: trustDevice,
      });
      // Backend nos devolvió tokens AAL2 — actualizar la session local del
      // SDK para que las siguientes llamadas usen el JWT con aal=aal2.
      await supabase.auth.setSession({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
      });
      // Forzar refresh para garantizar que la session del SDK quede en aal2.
      // Sin esto, el wrapper backend verify devuelve 200 pero la session
      // local del cliente sigue en aal1 → useMfaEnforcement nos rebota al
      // /challenge en loop. refreshSession() usa el refresh_token actual y
      // GoTrue emite el nuevo access_token reflejando el AAL elevado
      // server-side por la mfa.verify previa.
      await supabase.auth.refreshSession();
      toast.success('Verificación correcta');
      void navigate({ to: '/', replace: true });
    } catch (err) {
      const parsed = parseMfaError(err);
      if (parsed?.code === 'MFA_LOCKED_OUT' && parsed.retry_after_seconds) {
        setLockedUntilSec(parsed.retry_after_seconds);
        toast.error(parsed.message);
        return;
      }
      const remaining = parsed?.remaining_attempts;
      const msg =
        remaining !== undefined
          ? `Código incorrecto. Te ${remaining === 1 ? 'queda' : 'quedan'} ${remaining} ${remaining === 1 ? 'intento' : 'intentos'} antes del bloqueo de 15 minutos.`
          : (parsed?.message ?? 'Código incorrecto. Probá de nuevo.');
      toast.error(msg);
      setCode('');
    }
  };

  const locked = lockedUntilSec !== null && lockedUntilSec > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="totp-challenge-code">Código de 6 dígitos</Label>
        <Input
          id="totp-challenge-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          placeholder="123456"
          disabled={locked}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="text-center font-mono text-lg tracking-[0.5em]"
        />
      </div>

      {locked ? <CountdownNotice seconds={lockedUntilSec} onElapsed={() => setLockedUntilSec(null)} /> : null}

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          disabled={locked}
          checked={trustDevice}
          onChange={(e) => setTrustDevice(e.target.checked)}
        />
        <span>Confiar en este dispositivo por 30 días</span>
      </label>

      <Button type="submit" disabled={code.length !== 6 || verify.isPending || locked} className="w-full">
        {verify.isPending ? <Spinner size="sm" /> : 'Verificar'}
      </Button>
    </form>
  );
}

function BackupCodeForm() {
  const [code, setCode] = useState('');
  const [lockedUntilSec, setLockedUntilSec] = useState<number | null>(null);
  const navigate = useNavigate();
  const verify = useVerifyBackupCode();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!code.trim()) return;
    try {
      const res = await verify.mutateAsync({ plain_code: code.trim().toUpperCase() });
      await supabase.auth.setSession({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
      });
      if (res.backup_codes_remaining < 3) {
        toast.warning(
          `Te quedan ${res.backup_codes_remaining} códigos de respaldo. Regenerá una nueva tanda en Configuración → Seguridad.`,
          { duration: 8000 },
        );
      } else {
        toast.success('Sesión verificada con código de respaldo');
      }
      void navigate({ to: '/', replace: true });
    } catch (err) {
      const parsed = parseMfaError(err);
      if (parsed?.code === 'MFA_LOCKED_OUT' && parsed.retry_after_seconds) {
        setLockedUntilSec(parsed.retry_after_seconds);
        toast.error(parsed.message);
        return;
      }
      toast.error(parsed?.message ?? 'Código de respaldo inválido o ya usado.');
      setCode('');
    }
  };

  const locked = lockedUntilSec !== null && lockedUntilSec > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="backup-code">Código (formato XXXXX-XXXXX)</Label>
        <Input
          id="backup-code"
          autoComplete="off"
          autoFocus
          placeholder="ABCDE-12345"
          disabled={locked}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          className="text-center font-mono uppercase tracking-wider"
        />
      </div>

      {locked ? <CountdownNotice seconds={lockedUntilSec} onElapsed={() => setLockedUntilSec(null)} /> : null}

      <Button type="submit" disabled={!code.trim() || verify.isPending || locked} className="w-full">
        {verify.isPending ? <Spinner size="sm" /> : 'Verificar con código de respaldo'}
      </Button>
    </form>
  );
}

function CountdownNotice({
  seconds,
  onElapsed,
}: {
  readonly seconds: number;
  readonly onElapsed: () => void;
}) {
  const [remaining, setRemaining] = useState(seconds);
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;

  useEffect(() => {
    setRemaining(seconds);
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          onElapsedRef.current();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [seconds]);

  const mm = Math.floor(remaining / 60).toString().padStart(2, '0');
  const ss = (remaining % 60).toString().padStart(2, '0');

  return (
    <div
      className="rounded-md border p-3 text-center text-sm"
      style={{
        background: 'rgba(239, 68, 68, 0.06)',
        borderColor: 'rgba(239, 68, 68, 0.25)',
        color: 'rgb(185, 28, 28)',
      }}
    >
      <p>Demasiados intentos fallidos.</p>
      <p className="mt-1 font-mono text-lg tabular-nums">
        {mm}:{ss}
      </p>
    </div>
  );
}
